import { Link } from "react-router-dom";
import { CHURCH } from "@/lib/church";
import {
    ArrowRight,
    ArrowUpRight,
    HandHeart,
    Shield,
    Building,
    BookOpenText,
    UsersThree,
    Cross,
    Quotes,
    Envelope,
    PhoneCall,
    SealCheck,
} from "@phosphor-icons/react";

const PILLARS = [
    {
        icon: Cross,
        title: "Worship & Spiritual Formation",
        body: "Sustaining Sunday worship, sacred music, discipleship, and the preached Word across generations.",
    },
    {
        icon: UsersThree,
        title: "Community Outreach",
        body: "Feeding, clothing, and standing with our West Philadelphia neighbors through weekly food ministry and outreach.",
    },
    {
        icon: BookOpenText,
        title: "Education & Youth",
        body: "Investing in the next generation — Sunday School, YPD, mentorship, and scholarship support.",
    },
    {
        icon: Building,
        title: "Sanctuary Preservation",
        body: "Protecting the historic church home so that another generation may worship within these walls.",
    },
];

const TIERS = [
    {
        badge: "Tier I",
        name: "Sustaining Friend",
        gift: "Monthly Partnership",
        blurb: "A dependable rhythm of monthly support that steadies ministry throughout the year.",
        highlights: [
            "Recognition in the annual Friends Registry",
            "Quarterly Pastor's briefing",
            "Invitation to Founders' Sunday",
        ],
    },
    {
        badge: "Tier II",
        name: "Legacy Friend",
        gift: "Annual Patronage",
        blurb: "A distinguished annual commitment to the church's growth, mission, and dignity of worship.",
        highlights: [
            "Named plate acknowledgment",
            "Private reception with the Pastor",
            "Priority access to church programs",
        ],
        featured: true,
    },
    {
        badge: "Tier III",
        name: "Cornerstone Friend",
        gift: "Major & Planned Giving",
        blurb: "A defining gift toward capital, endowment, or planned bequest — investing in the next 100 years.",
        highlights: [
            "Personal stewardship planning",
            "Legacy Wall recognition",
            "Bequest & endowment guidance",
        ],
    },
];

export default function Friends() {
    return (
        <div data-testid="friends-page" className="bg-white">
            {/* HERO */}
            <section className="relative min-h-[85vh] flex items-center overflow-hidden bg-[#1a0f2e] text-white" data-testid="friends-hero">
                {/* Layered depth */}
                <div className="absolute inset-0">
                    <div className="absolute inset-0 bg-gradient-to-br from-[#2E1A47] via-[#1a0f2e] to-[#0e0721]" />
                    <div className="absolute -top-40 -left-40 w-[42rem] h-[42rem] bg-[#4C1D95]/30 blur-3xl rounded-full" />
                    <div className="absolute -bottom-40 -right-40 w-[42rem] h-[42rem] bg-[#FFC72C]/8 blur-3xl rounded-full" />
                    <div
                        className="absolute inset-0 opacity-[0.06] mix-blend-overlay"
                        style={{
                            backgroundImage:
                                "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='140' height='140'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
                        }}
                    />
                </div>

                <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-36 w-full">
                    <div className="max-w-4xl">
                        {/* Emblem line */}
                        <div className="flex items-center gap-4 mb-10 animate-fade-up">
                            <span className="w-14 h-px bg-[#FFC72C]" />
                            <p className="overline text-[#FFC72C]">A Registry of Stewardship &middot; Est. AME 1816</p>
                        </div>
                        <h1
                            className="font-display font-black tracking-tighter leading-[0.9] text-5xl sm:text-6xl lg:text-8xl xl:text-[8.5rem] animate-fade-up animate-delay-100"
                            data-testid="friends-title"
                        >
                            Friends of<br />
                            <span className="text-[#FFC72C] italic font-black">Greater Mount Olive</span><br />
                            AME Church.
                        </h1>
                        <p className="mt-10 text-lg lg:text-2xl text-white/75 max-w-3xl leading-relaxed font-light animate-fade-up animate-delay-200">
                            Sustaining over a <span className="text-[#FFC72C] font-medium">200-year legacy</span> of worship, service, and community leadership.
                        </p>
                        <div className="mt-12 flex flex-wrap gap-3 animate-fade-up animate-delay-300">
                            <a
                                href={CHURCH.giveUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="friends-become-btn"
                                className="inline-flex items-center gap-3 bg-[#FFC72C] hover:bg-[#E6B327] text-[#2E1A47] px-10 py-5 text-xs font-black uppercase tracking-[0.2em] transition-colors"
                            >
                                <HandHeart size={16} weight="bold" /> Become a Friend
                            </a>
                            <a
                                href="#stewardship"
                                data-testid="friends-read-more-btn"
                                className="inline-flex items-center gap-3 border border-white/25 hover:border-[#FFC72C] hover:text-[#FFC72C] text-white px-10 py-5 text-xs font-black uppercase tracking-[0.2em] transition-colors"
                            >
                                Read Our Commitment <ArrowRight size={14} weight="bold" />
                            </a>
                        </div>
                    </div>
                </div>

                {/* Bottom seal ribbon */}
                <div className="absolute bottom-0 inset-x-0 border-t border-white/10 bg-[#0e0721]/60 backdrop-blur-sm">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-wrap items-center gap-6 text-[10px] uppercase tracking-[0.25em] text-white/60 font-bold">
                        <span className="flex items-center gap-2"><SealCheck size={14} weight="duotone" className="text-[#FFC72C]" /> Historic AME Parish</span>
                        <span className="hidden sm:inline text-white/20">·</span>
                        <span>West Philadelphia</span>
                        <span className="hidden sm:inline text-white/20">·</span>
                        <span className="hidden md:inline">A Registry of Stewardship, Legacy & Service</span>
                    </div>
                </div>
            </section>

            {/* STEWARDSHIP STATEMENT */}
            <section id="stewardship" className="bg-[#F8F5F0] py-24 lg:py-32 relative overflow-hidden" data-testid="friends-stewardship">
                <div className="absolute top-0 right-0 w-72 h-72 bg-[#FFC72C]/10 blur-3xl rounded-full" />
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
                        <div className="lg:col-span-4">
                            <div className="lg:sticky lg:top-32">
                                <p className="overline text-[#4C1D95] mb-6">Section I</p>
                                <div className="flex items-start gap-4 mb-8">
                                    <Quotes size={44} weight="fill" className="text-[#FFC72C] shrink-0 -mt-1" />
                                    <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-[#2E1A47] leading-[0.95]">
                                        Our commitment<br />
                                        <span className="italic text-[#4C1D95]">to responsible</span><br />
                                        stewardship.
                                    </h2>
                                </div>
                                <div className="hidden lg:block pt-8 border-t border-[#2E1A47]/10">
                                    <p className="text-xs uppercase tracking-[0.25em] font-bold text-[#4C1D95] mb-3">Signed</p>
                                    <p className="font-display text-2xl italic text-[#2E1A47]">Rev. Marlo Woods, M.Div.</p>
                                    <p className="text-sm text-slate-600 mt-1">Senior Pastor</p>
                                </div>
                            </div>
                        </div>
                        <div className="lg:col-span-8">
                            <p className="font-display text-2xl lg:text-3xl leading-[1.55] text-[#2E1A47] tracking-tight mb-10">
                                Greater Mount Olive AME Church has served West Philadelphia for <span className="italic text-[#4C1D95]">over a decade</span> — providing spiritual formation, community stability, and social uplift to the neighborhoods we call home.
                            </p>
                            <div className="w-16 h-px bg-[#FFC72C] mb-10" />
                            <p className="text-base lg:text-lg leading-relaxed text-slate-700 mb-6">
                                As we enter our <span className="font-bold text-[#2E1A47]">third century of ministry</span> within the African Methodist Episcopal tradition, the <em className="text-[#4C1D95] font-medium">Friends of Greater Mount Olive AME Church</em> offer a structured platform for individuals, alumni, partners, and community stakeholders who wish to support the church&rsquo;s ongoing work.
                            </p>
                            <p className="text-base lg:text-lg leading-relaxed text-slate-700 mb-6">
                                We steward every gift with transparency, discipline, and reverence for the trust placed in us. Every offering — from the widow&rsquo;s mite to the cornerstone bequest — is directed toward the enduring mission of the AME church: <em className="text-[#2E1A47] font-medium">God our Father, Christ our Redeemer, the Holy Spirit our Comforter, and Humankind our Family.</em>
                            </p>
                            <p className="text-base lg:text-lg leading-relaxed text-slate-700">
                                The Friends Registry is not merely a donor list. It is a covenant — a community of partners who believe that a healthy, historic Black church remains one of the most consequential institutions in American public life.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* PILLARS — WHAT YOUR GIFT SUSTAINS */}
            <section className="bg-white py-24 lg:py-32" data-testid="friends-pillars">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-16 items-end">
                        <div className="lg:col-span-7">
                            <p className="overline text-[#4C1D95] mb-4">Section II &middot; Pillars of Stewardship</p>
                            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-[#2E1A47] leading-[0.95]">
                                What every friendship <span className="italic text-[#4C1D95]">sustains</span>.
                            </h2>
                        </div>
                        <p className="lg:col-span-5 text-slate-600 lg:text-right leading-relaxed">
                            Four pillars carry the weight of a two-hundred-year tradition. Each gift is directed to one of these four fields of ministry.
                        </p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 border-t border-l border-[#2E1A47]/10">
                        {PILLARS.map((p, i) => {
                            const Icon = p.icon;
                            return (
                                <div
                                    key={p.title}
                                    className="p-8 lg:p-10 border-r border-b border-[#2E1A47]/10 group hover:bg-[#F8F5F0] transition-colors"
                                    data-testid={`friends-pillar-${i}`}
                                >
                                    <div className="w-14 h-14 flex items-center justify-center bg-[#2E1A47] group-hover:bg-[#4C1D95] transition-colors mb-8">
                                        <Icon size={26} weight="duotone" className="text-[#FFC72C]" />
                                    </div>
                                    <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#4C1D95] mb-3">
                                        {String(i + 1).padStart(2, "0")} &middot; Pillar
                                    </p>
                                    <h3 className="font-display text-2xl font-black tracking-tight text-[#2E1A47] leading-tight mb-4">{p.title}</h3>
                                    <p className="text-sm text-slate-600 leading-relaxed">{p.body}</p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* TIERS */}
            <section className="bg-[#2E1A47] text-white py-24 lg:py-32 relative overflow-hidden" data-testid="friends-tiers">
                <div className="absolute -top-32 -left-32 w-[36rem] h-[36rem] bg-[#4C1D95]/50 blur-3xl rounded-full" />
                <div className="absolute -bottom-32 -right-32 w-[36rem] h-[36rem] bg-[#FFC72C]/10 blur-3xl rounded-full" />
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16 lg:mb-20 max-w-3xl mx-auto">
                        <p className="overline text-[#FFC72C] mb-4">Section III &middot; The Registry</p>
                        <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95] mb-6">
                            Three ways to <span className="italic text-[#FFC72C]">stand with us</span>.
                        </h2>
                        <p className="text-white/70 text-base lg:text-lg leading-relaxed">
                            Choose the covenant that suits your season. Every tier is honored — the smallest offering carries the same weight before God as the largest.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                        {TIERS.map((t, i) => (
                            <div
                                key={t.name}
                                data-testid={`friends-tier-${i}`}
                                className={`relative p-10 lg:p-12 flex flex-col ${
                                    t.featured
                                        ? "bg-[#FFC72C] text-[#2E1A47] lg:-mt-6 lg:mb-0 shadow-2xl"
                                        : "bg-white/[0.04] backdrop-blur-sm border border-white/10 text-white"
                                }`}
                            >
                                {t.featured && (
                                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#2E1A47] text-[#FFC72C] px-4 py-1 text-[10px] uppercase tracking-[0.25em] font-black whitespace-nowrap">
                                        Most Chosen
                                    </span>
                                )}
                                <div className={`flex items-center gap-3 mb-8 ${t.featured ? "text-[#2E1A47]" : "text-[#FFC72C]"}`}>
                                    <Shield size={18} weight="duotone" />
                                    <p className="text-[10px] uppercase tracking-[0.25em] font-black">{t.badge}</p>
                                </div>
                                <h3 className={`font-display text-4xl lg:text-5xl font-black tracking-tighter leading-[0.95] mb-3 ${t.featured ? "text-[#2E1A47]" : "text-white"}`}>
                                    {t.name}
                                </h3>
                                <p className={`text-xs uppercase tracking-[0.25em] font-bold mb-6 ${t.featured ? "text-[#4C1D95]" : "text-[#FFC72C]"}`}>
                                    {t.gift}
                                </p>
                                <p className={`text-sm lg:text-base leading-relaxed mb-8 ${t.featured ? "text-[#2E1A47]/85" : "text-white/75"}`}>
                                    {t.blurb}
                                </p>
                                <ul className={`space-y-3 mb-10 flex-1 ${t.featured ? "text-[#2E1A47]/85" : "text-white/80"}`}>
                                    {t.highlights.map((h) => (
                                        <li key={h} className="flex items-start gap-3 text-sm">
                                            <SealCheck size={16} weight="duotone" className={`shrink-0 mt-0.5 ${t.featured ? "text-[#4C1D95]" : "text-[#FFC72C]"}`} />
                                            <span>{h}</span>
                                        </li>
                                    ))}
                                </ul>
                                <a
                                    href={CHURCH.giveUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid={`friends-tier-cta-${i}`}
                                    className={`inline-flex items-center justify-center gap-2 px-6 py-4 text-xs font-black uppercase tracking-[0.2em] transition-colors ${
                                        t.featured
                                            ? "bg-[#2E1A47] hover:bg-[#4C1D95] text-[#FFC72C]"
                                            : "bg-[#FFC72C] hover:bg-[#E6B327] text-[#2E1A47]"
                                    }`}
                                >
                                    Enroll <ArrowUpRight size={14} weight="bold" />
                                </a>
                            </div>
                        ))}
                    </div>

                    <p className="text-center text-white/50 text-xs mt-12 max-w-2xl mx-auto">
                        Gifts to the Friends Registry may be tax-deductible to the fullest extent allowed by law. For planned gifts, endowments, or legal arrangements, contact the Senior Pastor directly.
                    </p>
                </div>
            </section>

            {/* CTA */}
            <section className="bg-[#F8F5F0] py-24 lg:py-32" data-testid="friends-cta">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                        <div className="lg:col-span-7">
                            <p className="overline text-[#4C1D95] mb-5">Join the Registry</p>
                            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-[#2E1A47] leading-[0.95] mb-8">
                                Stand with a<br />
                                <span className="italic text-[#4C1D95]">two-hundred-year</span><br />
                                congregation.
                            </h2>
                            <p className="text-base lg:text-lg text-slate-700 leading-relaxed max-w-xl mb-10">
                                Whether you were baptized in these walls, watched a loved one buried from this pulpit, or simply believe in what a faithful Black church builds in a neighborhood — we invite you to become a Friend of Greater Mount Olive.
                            </p>
                            <div className="flex flex-wrap gap-3">
                                <a
                                    href={CHURCH.giveUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="friends-cta-give"
                                    className="inline-flex items-center gap-3 bg-[#2E1A47] hover:bg-[#4C1D95] text-[#FFC72C] px-10 py-5 text-xs font-black uppercase tracking-[0.2em] transition-colors"
                                >
                                    <HandHeart size={16} weight="bold" /> Give Today
                                </a>
                                <Link
                                    to="/contact"
                                    data-testid="friends-cta-contact"
                                    className="inline-flex items-center gap-3 border-2 border-[#2E1A47] hover:bg-[#2E1A47] hover:text-[#FFC72C] text-[#2E1A47] px-10 py-5 text-xs font-black uppercase tracking-[0.2em] transition-colors"
                                >
                                    Speak With the Pastor
                                </Link>
                            </div>
                        </div>
                        <div className="lg:col-span-5">
                            <div className="bg-white p-10 shadow-lg">
                                <p className="overline text-[#4C1D95] mb-6">Direct Line</p>
                                <div className="space-y-6">
                                    <div className="flex items-start gap-4">
                                        <PhoneCall size={22} weight="duotone" className="text-[#4C1D95] shrink-0 mt-1" />
                                        <div>
                                            <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500 mb-1">Church Office</p>
                                            <a href={CHURCH.phoneHref} data-testid="friends-phone" className="text-lg font-display font-bold text-[#2E1A47] hover:text-[#4C1D95]">
                                                {CHURCH.phone}
                                            </a>
                                        </div>
                                    </div>
                                    <div className="flex items-start gap-4">
                                        <Envelope size={22} weight="duotone" className="text-[#4C1D95] shrink-0 mt-1" />
                                        <div>
                                            <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500 mb-1">Email</p>
                                            <a href={CHURCH.emailHref} data-testid="friends-email" className="text-base font-display font-bold text-[#2E1A47] hover:text-[#4C1D95] break-all">
                                                {CHURCH.email}
                                            </a>
                                        </div>
                                    </div>
                                    <div className="pt-6 border-t border-[#2E1A47]/10">
                                        <p className="text-xs text-slate-500 leading-relaxed">
                                            For planned giving, endowments, memorial gifts, or matching-fund arrangements, please request a private consultation with the Senior Pastor.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
